import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Share2, FileText, CalendarClock, ChevronRight, Users, Calendar, MessageCircle, Sparkles } from "lucide-react";
import AddPatientDialog from "@/components/dialogs/AddPatientDialog";
import BookAppointmentDialog from "@/components/dialogs/BookAppointmentDialog";
import NewConsultationDialog from "@/components/dialogs/NewConsultationDialog";
import ReferPatientDialog from "@/components/dialogs/ReferPatientDialog";
import PracticeInsights from "@/components/PracticeInsights";
import NudgeCentre from "@/components/NudgeCentre";
import { ageDisplay, initials, displayTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const TABS = [
  { key: "grow", label: "Get more out of HealthCircles", shortLabel: "Grow", icon: Sparkles },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "referrals", label: "Referrals", icon: Share2 },
  { key: "patients", label: "Patients", icon: Users },
  { key: "chat", label: "Chat", icon: MessageCircle },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("grow");
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [summary, setSummary] = useState({ appointments: 0, follow_ups: 0, referrals: 0, patients: 0 });
  const [openAdd, setOpenAdd] = useState(false);
  const [openNewConsult, setOpenNewConsult] = useState(false);
  const [openBook, setOpenBook] = useState(false);
  const [openRefer, setOpenRefer] = useState(false);
  const [activePatient, setActivePatient] = useState(null);

  const loadAll = async () => {
    try {
      const [pRes, aRes, rRes, sRes] = await Promise.all([
        api.get("/patients"),
        api.get("/appointments"),
        api.get("/referrals"),
        api.get("/dashboard/summary"),
      ]);
      setPatients(pRes.data);
      setAppts(aRes.data);
      setReferrals(rRes.data);
      setSummary(sRes.data);
    } catch (e) { toast.error("Failed to load dashboard"); }
  };

  useEffect(() => { loadAll(); }, []);

  const filteredPatients = useMemo(() => {
    if (!q.trim()) return patients;
    const s = q.toLowerCase();
    return patients.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.mobile || "").includes(s));
  }, [q, patients]);

  const today = new Date().toISOString().slice(0, 10);
  // Dashboard's Appointments tab shows TODAY + upcoming (not the full historical list)
  // so newly booked future appointments are immediately discoverable here.
  const todaysAppts = useMemo(
    () => appts
      .filter((a) => a.date >= today && a.status !== "cancelled")
      .sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time))
      .slice(0, 20),
    [appts, today]
  );

  return (
    <Layout>
      {/* Hero row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
        <div className="lg:col-span-3 card-hero-peach relative overflow-hidden p-8 md:p-10" data-testid="hero-welcome">
          <div className="absolute -left-8 -top-8 w-40 h-40 bg-white/30 rounded-full blur-2xl" />
          <div className="relative">
            <div className="field-label text-[#8A5A34] mb-2">Welcome back</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#4A2C13]" style={{ fontFamily: "Instrument Sans" }}>
              Dr. {user?.name?.replace(/^Dr\.?\s*/i, "") || ""}
            </h1>
            <p className="mt-3 text-[#5C3A21]/90 text-base">
              You have <b>{summary.appointments}</b> appointments,{" "}
              <b>{summary.follow_ups}</b> follow-ups & <b>{summary.referrals}</b> referrals today.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="btn-emerald" onClick={() => setOpenAdd(true)} data-testid="hero-add-patient">
                <Plus size={16} /> Add new patient
              </button>
              <button className="btn-emerald" onClick={() => setOpenNewConsult(true)} data-testid="hero-new-consult">
                <FileText size={16} /> New consultation
              </button>
              <button className="btn-outline-emerald" onClick={() => nav("/appointments")} data-testid="hero-view-appts">
                <CalendarClock size={16} /> Today's schedule
              </button>
            </div>
          </div>
        </div>
        <div className="card-soft p-6 flex flex-col justify-between" data-testid="hero-stats">
          <div className="field-label text-[#0A4C36]">Today at a glance</div>
          <div className="mt-4 space-y-3">
            <Stat label="Patients" value={summary.patients} testId="stat-patients" />
            <Stat label="Appointments" value={summary.appointments} testId="stat-appointments" />
            <Stat label="Follow-ups" value={summary.follow_ups} testId="stat-follow-ups" />
            <Stat label="Referrals" value={summary.referrals} testId="stat-referrals" />
          </div>
        </div>
      </div>

      {/* Practice insights */}
      <PracticeInsights />

      {/* Tabs card */}
      <div className="card-soft p-4 sm:p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-none">
            <div className="flex items-center gap-1 flex-nowrap md:flex-wrap">
              {TABS.map((t) => {
                const active = tab === t.key;
                const count =
                  t.key === "grow" ? null :
                  t.key === "appointments" ? todaysAppts.length :
                  t.key === "referrals" ? referrals.length :
                  t.key === "patients" ? patients.length : 0;
                return (
                  <button
                    key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`pill-tab flex-none ${active ? "pill-tab-active" : "pill-tab-inactive"}`}
                  >
                    <t.icon size={14} strokeWidth={1.75} />
                    <span className="md:hidden">{t.shortLabel || t.label}</span>
                    <span className="hidden md:inline">{t.label}</span>
                    {count !== null && (
                      <span className={`tab-badge ${active ? "bg-white/20 text-white" : "bg-[#0A4C36]/8 text-[#0A4C36]"}`}>
                        {String(count).padStart(2, "0")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="relative w-full md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]" />
            <input
              data-testid="dashboard-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              className="input-clean pl-10"
            />
          </div>
        </div>

        <div className="divider-soft my-4 sm:my-6" />

        {tab === "grow" && <NudgeCentre />}
        {tab === "patients" && (
          <PatientsTab
            patients={filteredPatients}
            onOpen={(p) => nav(`/patients/${p.id}`)}
            onBook={(p) => { setActivePatient(p); setOpenBook(true); }}
            onRefer={(p) => { setActivePatient(p); setOpenRefer(true); }}
          />
        )}
        {tab === "appointments" && <AppointmentsTab appts={todaysAppts} onOpen={(a) => nav(`/consultation/new?patient=${a.patient_id}&appt=${a.id}`)} />}
        {tab === "referrals" && <ReferralsTab items={referrals} />}
        {tab === "chat" && <EmptyState title="Chat is coming soon" subtitle="Send follow-ups and reminders directly to patients." />}
      </div>

      <AddPatientDialog open={openAdd} onOpenChange={setOpenAdd} onCreated={() => loadAll()} />
      <NewConsultationDialog open={openNewConsult} onOpenChange={setOpenNewConsult} />
      <BookAppointmentDialog open={openBook} onOpenChange={setOpenBook} patient={activePatient} onBooked={() => loadAll()} />
      <ReferPatientDialog open={openRefer} onOpenChange={setOpenRefer} patient={activePatient} onDone={() => loadAll()} />
    </Layout>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className="text-sm text-[#6C7B72]">{label}</span>
      <span className="text-2xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>{String(value).padStart(2, "0")}</span>
    </div>
  );
}

function PatientsTab({ patients, onOpen, onBook, onRefer }) {
  const startQuickConsult = async (patient) => {
    try {
      const { data } = await api.post("/appointments/quick", { patient_id: patient.id });
      window.location.href = `/consultation/new?patient=${patient.id}&appt=${data.id}`;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start consultation");
    }
  };
  if (!patients.length)
    return <EmptyState title="No patients yet" subtitle="Add your first patient using the button above." />;
  return (
    <div>
      <div className="field-label mb-4">Sorted A–Z</div>
      <div className="space-y-2">
        {patients.map((p) => (
          <div key={p.id} className="group flex items-center gap-2 sm:gap-4 rounded-2xl px-3 sm:px-4 py-3 sm:py-4 row-hover transition-colors" data-testid={`patient-row-${p.id}`}>
            <button className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 text-left" onClick={() => onOpen(p)}>
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold text-sm flex-none">
                {initials(p.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1F2A24] text-sm sm:text-base truncate inline-flex items-center gap-2 flex-wrap">
                  {p.name}
                  {p.referred_by_doctor_name && (
                    <span
                      title={`Referred by ${p.referred_by_doctor_name}`}
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#E6F0EC] text-[#0A4C36] uppercase tracking-widest"
                      data-testid={`patient-referred-${p.id}`}
                    >
                      Referred
                    </span>
                  )}
                </div>
                <div className="text-[11px] sm:text-xs text-[#8A9992] mt-0.5 truncate">{p.gender}, {ageDisplay(p)}</div>
              </div>
              <div className="hidden md:block text-sm text-[#47554D] flex-none">{p.mobile || "—"}</div>
            </button>
            <div className="flex items-center gap-1 sm:gap-2 flex-none">
              <button title="Start consult" onClick={() => startQuickConsult(p)} data-testid={`patient-consult-${p.id}`} className="w-9 h-9 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#D18A3E] flex-none">
                <FileText size={16} strokeWidth={1.75} />
              </button>
              <button title="Book appointment" onClick={() => onBook(p)} data-testid={`patient-book-${p.id}`} className="w-9 h-9 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#0A4C36] flex-none">
                <CalendarClock size={16} strokeWidth={1.75} />
              </button>
              <button title="Refer" onClick={() => onRefer(p)} data-testid={`patient-refer-${p.id}`} className="btn-outline-emerald px-2.5 sm:px-4 py-1.5 flex-none">
                <Share2 size={14} /> <span className="hidden sm:inline">Refer</span>
              </button>
              <button className="hidden sm:flex w-9 h-9 rounded-full hover:bg-[#F3F1EC] items-center justify-center text-[#8A9992] flex-none" onClick={() => onOpen(p)}>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppointmentsTab({ appts, onOpen }) {
  if (!appts.length)
    return <EmptyState title="No upcoming appointments" subtitle="Book new appointments from patient details." />;
  const todayISO = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-2">
      {appts.map((a) => (
        <button key={a.id} onClick={() => onOpen(a)} data-testid={`appt-row-${a.id}`} className="w-full flex items-center gap-3 sm:gap-4 rounded-2xl px-3 sm:px-4 py-3 sm:py-4 row-hover text-left transition-colors">
          <div className="w-14 sm:w-16 flex-none rounded-2xl bg-[#FCE7D9] text-[#5C3A21] flex flex-col items-center justify-center py-2">
            <div className="text-[10px] tracking-widest uppercase">TIME</div>
            <div className="text-sm font-semibold whitespace-nowrap">{displayTime(a.time)}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#1F2A24] text-sm sm:text-base truncate inline-flex items-center gap-2">
              {a.patient_name}
              {a.date === todayISO && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#0A4C36] text-white uppercase tracking-widest">Today</span>}
            </div>
            <div className="text-[11px] sm:text-xs text-[#8A9992] mt-0.5 truncate">
              {a.date} · {a.mode} · {a.status}
            </div>
          </div>
          <ChevronRight size={16} className="text-[#8A9992] flex-none" />
        </button>
      ))}
    </div>
  );
}

function ReferralsTab({ items }) {
  if (!items.length) return <EmptyState title="No referrals yet" subtitle="Refer a patient from the Patients tab." />;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className="rounded-2xl px-4 py-4 row-hover" data-testid={`referral-row-${r.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-[#1F2A24]">{r.patient_name} → {r.doctor_name || "—"}</div>
              <div className="text-xs text-[#8A9992] mt-0.5">{r.specialization}</div>
            </div>
            <div className="chip">{new Date(r.created_at).toLocaleDateString()}</div>
          </div>
          {r.notes && <div className="mt-2 text-sm text-[#47554D]">{r.notes}</div>}
        </div>
      ))}
    </div>
  );
}


function EmptyState({ title, subtitle }) {
  return (
    <div className="py-16 text-center">
      <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>{title}</div>
      <div className="text-sm text-[#8A9992] mt-2">{subtitle}</div>
    </div>
  );
}
